import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const statusBadge = (s) => {
  const map = { pending:'badge-yellow', approved:'badge-green', rejected:'badge-red' };
  return <span className={`badge ${map[s]||'badge-gray'}`}>{s}</span>;
};

export default function Approvals() {
  const [tab, setTab]                 = useState('leave');
  const [leaves, setLeaves]           = useState([]);
  const [wfhs, setWfhs]               = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [note, setNote]               = useState('');
  const [loading, setLoading]         = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const [l, w, c] = await Promise.all([
      api.get('/leave/pending'),
      api.get('/wfh/pending'),
      api.get('/corrections/pending'),
    ]);
    setLeaves(l.data);
    setWfhs(w.data);
    setCorrections(c.data);
  };

  const act = async (type, id, action, extraNote) => {
    setLoading(true);
    try {
      if (type === 'leave') {
        await api.put(`/leave/${id}/${action}`, { approval_note: extraNote });
      } else if (type === 'wfh') {
        await api.put(`/wfh/${id}/${action}`);
      } else {
        await api.put(`/corrections/${id}/${action}`);
      }
      toast.success(`${type.toUpperCase()} ${action}d`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Action failed');
    } finally { setLoading(false); }
  };

  const totalPending = leaves.length + wfhs.length + corrections.length;

  const TabBtn = ({ id, label, count }) => (
    <button onClick={() => setTab(id)}
      className={`btn ${tab===id ? 'btn-primary' : 'btn-outline'}`}>
      {label} {count > 0 && <span style={{background:'var(--danger)',color:'white',borderRadius:'50%',padding:'1px 6px',fontSize:11,marginLeft:4}}>{count}</span>}
    </button>
  );

  return (
    <div>
      <div className="flex-between mb-16">
        <h1 className="page-title" style={{marginBottom:0}}>
          Approvals {totalPending > 0 && <span style={{color:'var(--danger)',fontSize:16}}>({totalPending} pending)</span>}
        </h1>
      </div>

      <div className="flex gap-8 mb-16">
        <TabBtn id="leave"       label="Leave"       count={leaves.length}/>
        <TabBtn id="wfh"         label="WFH"         count={wfhs.length}/>
        <TabBtn id="corrections" label="Corrections" count={corrections.length}/>
      </div>

      {tab === 'leave' && (
        <div className="card">
          <div className="card-title">Pending Leave Applications</div>
          {leaves.length === 0 ? <div className="empty-state"><div className="icon">✅</div><p>No pending leave applications</p></div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Applied</th><th>Actions</th></tr></thead>
                <tbody>
                  {leaves.map(l => (
                    <tr key={l.id}>
                      <td><div style={{fontWeight:500}}>{l.full_name}</div><div className="text-sm text-muted">{l.department}</div></td>
                      <td>{l.leave_type_name}</td>
                      <td>{new Date(l.from_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</td>
                      <td>{new Date(l.to_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</td>
                      <td>{l.total_days}{l.half_day!=='none'?` (${l.half_day})`:''}</td>
                      <td style={{maxWidth:200,overflow:'hidden',textOverflow:'ellipsis'}}>{l.reason||'—'}</td>
                      <td>{new Date(l.applied_at).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div className="flex gap-8">
                          <button className="btn btn-success btn-sm" disabled={loading}
                            onClick={() => act('leave', l.id, 'approve', '')}>Approve</button>
                          <button className="btn btn-danger btn-sm" disabled={loading}
                            onClick={() => act('leave', l.id, 'reject', '')}>Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'wfh' && (
        <div className="card">
          <div className="card-title">Pending WFH Requests</div>
          {wfhs.length === 0 ? <div className="empty-state"><div className="icon">✅</div><p>No pending WFH requests</p></div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Employee</th><th>Date</th><th>Reason</th><th>Applied</th><th>Actions</th></tr></thead>
                <tbody>
                  {wfhs.map(w => (
                    <tr key={w.id}>
                      <td><div style={{fontWeight:500}}>{w.full_name}</div><div className="text-sm text-muted">{w.department}</div></td>
                      <td>{new Date(w.date).toLocaleDateString('en-IN',{weekday:'short',day:'2-digit',month:'short',year:'numeric'})}</td>
                      <td>{w.reason||'—'}</td>
                      <td>{new Date(w.applied_at).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div className="flex gap-8">
                          <button className="btn btn-success btn-sm" disabled={loading}
                            onClick={() => act('wfh', w.id, 'approve')}>Approve</button>
                          <button className="btn btn-danger btn-sm" disabled={loading}
                            onClick={() => act('wfh', w.id, 'reject')}>Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'corrections' && (
        <div className="card">
          <div className="card-title">Pending Correction Requests</div>
          {corrections.length === 0 ? <div className="empty-state"><div className="icon">✅</div><p>No pending correction requests</p></div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Employee</th><th>Date</th><th>Requested In</th><th>Requested Out</th><th>Reason</th><th>Actions</th></tr></thead>
                <tbody>
                  {corrections.map(c => (
                    <tr key={c.id}>
                      <td style={{fontWeight:500}}>{c.full_name}</td>
                      <td>{new Date(c.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</td>
                      <td>{c.requested_check_in ? new Date(c.requested_check_in).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                      <td>{c.requested_check_out ? new Date(c.requested_check_out).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                      <td>{c.reason}</td>
                      <td>
                        <div className="flex gap-8">
                          <button className="btn btn-success btn-sm" disabled={loading}
                            onClick={() => act('corrections', c.id, 'approve')}>Approve</button>
                          <button className="btn btn-danger btn-sm" disabled={loading}
                            onClick={() => act('corrections', c.id, 'reject')}>Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
